import type { FunctionComponent } from 'react'
import React from 'react'
import { TypographyV2 as Typography } from '@which/seatbelt'
import { ExternalLinkIcon } from '@which/seatbelt/src/components/Icons/Navigational/ExternalLink'
import { dynamicGa4DataLayerPush } from '@which/shared'

import type { AffiliateItem } from '../../../../generated/frontend'
import { TrackonomicsLink } from '../../../../shared'
import styles from './AffiliateLinksList.module.scss'

export interface Props {
  affiliateItems: AffiliateItem[]
  manufacturer: string
  model: string
}

type AffiliateClickItem = Omit<AffiliateItem, 'isTrackable'>

export const AffiliateLinksList: FunctionComponent<Props> = ({
  affiliateItems,
  manufacturer,
  model,
}) => {
  const sharedTrackingData = {
    eventAction: 'Go to Retailer',
    eventCategory: 'Where to Buy',
    item_group: 'which-picks',
  }
  const handleAffiliateClick = ({ price, retailer, url }: AffiliateClickItem) =>
    dynamicGa4DataLayerPush({
      ...sharedTrackingData,
      event: 'tEvent',
      eventLabel: `${retailer} | ${model} | ${price}`,
      item_spec: new URL(url).hostname,
      item_url: url,
    })
  const LinkChildren = ({ item }: { item: AffiliateItem }) => (
    <>
      <Typography tag="span" textStyle="sb-text-interface-body-small-regular">
        {item.retailer}
      </Typography>
      <span className={styles.priceWrapper}>
        <Typography tag="span" textStyle="sb-text-body-small-regular" className={styles.price}>
          {item.price}
        </Typography>
        <ExternalLinkIcon className={styles.externalLinkIcon} />
      </span>
    </>
  )

  return (
    <div className={styles.wrapper}>
      <ol className={styles.linksList}>
        {affiliateItems.map((item, index) => {
          const commonAnchorProps = {
            className: styles.listLink,
            target: '_blank',
            'aria-label': `Buy from ${manufacturer} at ${item.price}`,
            rel: 'nofollow noopener noreferrer',
          }

          return (
            <li key={`${item.retailer}-${index}`}>
              {item.isTrackable ? (
                <TrackonomicsLink
                  {...commonAnchorProps}
                  href={item.url}
                  useLinkComponent={false}
                  contentType="shop home"
                  taxonomyHierarchy={{
                    vertical: 'which-picks',
                    subVertical: '',
                    category: '',
                    superCategory: '',
                  }}
                  optionalTracking={{
                    ...sharedTrackingData,
                    item_text: item.retailer,
                    eventLabel: `${item.retailer} | ${model} | ${item.price}`,
                    item_spec: new URL(item.url).hostname,
                    item_url: item.url,
                  }}
                  onClick={() => handleAffiliateClick(item)}
                >
                  <LinkChildren item={item} />
                </TrackonomicsLink>
              ) : (
                <a
                  {...commonAnchorProps}
                  href={item.url}
                  data-testid="affiliate-link"
                  data-which-id="affiliate-link"
                  onClick={() => handleAffiliateClick(item)}
                >
                  <LinkChildren item={item} />
                </a>
              )}
            </li>
          )
        })}
      </ol>
    </div>
  )
}
